import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center" data-testid="error-boundary-fallback">
          <AlertTriangle className="h-12 w-12 text-orange-500 mb-4" />
          <h2 className="text-lg font-semibold mb-2">Something went wrong</h2>
          <p className="text-sm text-muted-foreground mb-4 max-w-md">
            An unexpected error occurred. You can try again or navigate to a different page.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={this.handleReset} data-testid="button-try-again">
              Try Again
            </Button>
            <Button variant="default" onClick={() => { window.location.href = "/"; }} data-testid="button-go-home" className="bg-teal-600 hover:bg-teal-700">
              Go to Dashboard
            </Button>
          </div>
          {this.state.error && (
            <details className="mt-4 text-left max-w-lg">
              <summary className="text-xs text-muted-foreground cursor-pointer">Technical details</summary>
              <pre className="mt-2 text-xs bg-muted p-3 rounded overflow-auto max-h-40">{this.state.error.message}</pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
